//@ts-strict-ignore
import { ResponsiveTreeMap } from '@nivo/treemap';
import { ResponsiveCirclePacking } from '@nivo/circle-packing';
import { ResponsiveSunburst } from '@nivo/sunburst';
import React, { useState } from 'react';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import { IWrappedMeasure } from './SalesAnalyticsTypes';
import { AnalysisGraphType, AnalysisMeasure } from '../../../graphql/schema/p4types';
import { SecondaryButton } from '../../../components/connectedForm/Button';

type ISalesAnalyticsTreeProps = {
  pivotRef: any;
  formState: {
    measure: IWrappedMeasure | undefined;
    graphType: AnalysisGraphType;
    pivotRows?: Array<{ description: string }>;
  };
  colorScheme?: OrdinalColorScaleConfig;
};

/**
 * Build Tree Data from Pivot Table current configuration & display a TreeMap of the results.
 * @param pivotRef  Pivot Table to draw data from.
 * @param measure   Measure to display - Should be Sales, Cost or Units. NOT PROFIT - Negative Values are not supported and will result in an invalid graph.
 * @param graphType Options: [BUBBLE, MAP] - Graph Types for Sales Analytics.
 * @param data      Pivot Table Rows.
 */
export const SalesAnalyticsTree = ({
  pivotRef,
  formState: { measure = { description: 'Sales', value: AnalysisMeasure.Sales }, graphType, pivotRows },
  colorScheme: colors = { scheme: 'set3' },
}: ISalesAnalyticsTreeProps) => {
  const [zoomedId, setZoomedId] = useState<string | null>(null);

  if (!pivotRef.current) return null;

  if ((pivotRows?.length ?? 0) > 5000) {
    return (
      <div style={{ padding: '1em' }}>
        <div>Too many rows ({pivotRows?.length}) - Graph has been hidden.</div>
      </div>
    );
  }

  const buildMap = (dimensions, pivotRows, path = '') => {
    const dimension = dimensions[0];
    const dimensionMap = {};
    pivotRows.forEach((pivotRow) => {
      dimensionMap[pivotRow[dimension]] = pivotRow;
    });
    return Object.keys(dimensionMap).map((dimensionKey) => {
      if (dimensions.length > 1) {
        return {
          id: `${path} - ${dimensionKey}`,
          name: dimensionKey,
          children: buildMap(
            dimensions.slice(1),
            pivotRows.filter((pivotRow) => pivotRow[dimension] === dimensionKey),
            path.length > 0 ? `${path} - ${dimensionKey}` : dimensionKey
          ),
        };
      }
      return {
        id: `${path} - ${dimensionKey}`,
        name: dimensionKey,
        loc: dimensionMap[dimensionKey][measure.value.toLowerCase()],
      };
    });
  };

  const root = {
    name: measure.description,
    children: buildMap(pivotRef.current.state.dimensions, pivotRows),
  };

  if (graphType === 'MAP') {
    return (
      <div style={{ height: 300, width: '100%' }}>
        <ResponsiveTreeMap
          data={root}
          identity="id"
          value="loc"
          innerPadding={10}
          outerPadding={6}
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
          label="loc"
          valueFormat="^.0s"
          labelSkipSize={12}
          colors={colors}
          animate={true}
        />
      </div>
    );
  } else if (graphType === 'BUBBLE') {
    return (
      <div style={{ height: 500, width: '100%' }}>
        <ResponsiveCirclePacking
          data={root}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          id="id"
          value="loc"
          colors={colors}
          padding={6}
          borderWidth={2}
          defs={[
            {
              id: 'lines',
              type: 'patternLines',
              background: 'none',
              color: 'inherit',
              rotation: -45,
              lineWidth: 5,
              spacing: 8,
            },
          ]}
          fill={[{ match: { depth: 1 }, id: 'lines' }]}
          animate={true}
          motionConfig="gentle"
          zoomedId={zoomedId}
          onClick={(node) => {
            setZoomedId(zoomedId === node.id ? null : node.id);
          }}
        />
      </div>
    );
  }
  return (
    <div style={{ height: 500, width: '100%' }}>
      <ResponsiveSunburst
        data={root}
        margin={{ top: 40, right: 20, bottom: 20, left: 20 }}
        id="id"
        value="loc"
        cornerRadius={2}
        borderWidth={1}
        borderColor="white"
        colors={colors}
        animate={true}
        motionConfig={'stiff'}
        isInteractive={true}
      />
    </div>
  );
};
